import React, { useContext } from "react";
import styled from "@emotion/styled";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import HCheckoutForm from "./CheckoutForm";
import { CartContext } from './contexts/CartContext';
import { formatNumber } from '../utils';

// Make sure to call loadStripe outside of a component’s render to avoid
// recreating the Stripe object on every render.
// loadStripe is initialized with your real test publishable API key.
//const promise = loadStripe("pk_test_51HMN3WDLo01jcMScC9EcVCGNoKTyeT93A7QxNv29Kg1GXbf8cy28W1Rg4MFRp6cRykwA4s3VNyTHYxPzKsdETMkv00rGivhOz3");
const promiselive = loadStripe("pk_live_51HMN3WDLo01jcMScKcC6gdTuJxQOiJU0rWbJRsSOsrM7Mis0LAiPTS6XBFsQPn49BYZjWlIFimRbhtrjdfJiDX1Y00Kjp8zNW5")

export default function HPayList() {
  const { total, tax, handleCheckout, tprice, cartItems, itemCount } = useContext(CartContext);
  const [mobileSummaryOpen, setMobileSummaryOpen] = React.useState(false);
  const [appliedPromo, setAppliedPromo] = React.useState(null);

  const discountAmount = appliedPromo ? appliedPromo.discountAmount : 0;
  const grandTotal = Math.max(0, tprice - discountAmount);

  return (
    <Page>
      <Title>Checkout</Title>

      {/* Mobile-only Summary Accordion Toggle (< 900px) */}
      <MobileSummaryBar type="button" onClick={() => setMobileSummaryOpen(!mobileSummaryOpen)}>
        <span>🛒 Order Summary ({itemCount} item{itemCount > 1 ? 's' : ''})</span>
        <strong>{formatNumber(grandTotal)} {mobileSummaryOpen ? '▲' : '▼'}</strong>
      </MobileSummaryBar>

      <Grid>
        <FormCol>
          <Elements stripe={promiselive}>
            <HCheckoutForm
              price={tprice}
              total={total}
              tax={tax}
              onDiscountChange={(promoInfo) => setAppliedPromo(promoInfo.discountAmount > 0 ? promoInfo : null)}
              onSuccessfulCheckout={() => {
                handleCheckout();
                window.location.href = "/";
              }}
            />
          </Elements>
        </FormCol>

        <SummaryCol $openMobile={mobileSummaryOpen}>
          <SummaryCard>
            <SummaryHead>
              Order summary
              {itemCount > 0 && <Count>{itemCount}</Count>}
            </SummaryHead>

            <Items>
              {cartItems && cartItems.map(item => (
                <LineItem key={item.id}>
                  <LiName>
                    {item.name || item.description}
                    <LiQty>× {item.quantity}</LiQty>
                  </LiName>
                  <LiPrice>{formatNumber(item.price * item.quantity)}</LiPrice>
                </LineItem>
              ))}
            </Items>

            <Divider />
            <Row>
              <span>Subtotal</span>
              <strong>{formatNumber(total)}</strong>
            </Row>
            <Row>
              <span>Tax</span>
              <strong>{formatNumber(tax)}</strong>
            </Row>
            {discountAmount > 0 && (
              <Row>
                <span style={{ color: "#0a8f57", fontWeight: 600 }}>Discount ({appliedPromo.code})</span>
                <strong style={{ color: "#0a8f57" }}>- {formatNumber(discountAmount)}</strong>
              </Row>
            )}
            <Divider />
            <Row className="grand">
              <span>Total</span>
              <strong>{formatNumber(grandTotal)}</strong>
            </Row>
          </SummaryCard>
        </SummaryCol>
      </Grid>
    </Page>
  );
}


/* ---------- styles ---------- */
const Page = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  padding: 24px 14px 48px;

  @media screen and (min-width: 480px) {
    padding: 32px 20px 64px;
  }
`;

const Title = styled.h1`
  margin: 0 0 24px;
  font-size: clamp(1.6rem, 3.5vw, 2.2rem);
  font-weight: 800;
  color: #1f2d3d;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  @media screen and (min-width: 900px) {
    grid-template-columns: minmax(0, 1fr) 360px;
    align-items: start;
  }
`;

const MobileSummaryBar = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 14px 18px;
  background: rgba(1, 191, 113, 0.08);
  border: 1px solid rgba(1, 191, 113, 0.2);
  border-radius: 12px;
  color: #0a8f57;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  margin-bottom: 18px;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(1, 191, 113, 0.14);
  }

  @media screen and (min-width: 900px) {
    display: none;
  }
`;

const FormCol = styled.div`
  min-width: 0;
`;

const SummaryCol = styled.div`
  display: ${props => (props.$openMobile ? 'block' : 'none')};

  @media screen and (min-width: 900px) {
    display: block;
    position: sticky;
    top: 100px;
  }
`;

const SummaryCard = styled.div`
  background: #fff;
  border: 1px solid rgba(31, 45, 61, 0.08);
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 10px 26px rgba(31, 45, 61, 0.07);
`;

const SummaryHead = styled.h3`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 16px;
  font-size: 1.1rem;
  font-weight: 800;
  color: #1f2d3d;
`;

const Count = styled.span`
  background: rgba(1, 191, 113, 0.12);
  color: #0a8f57;
  font-size: 0.8rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
`;

const Items = styled.div`
  display: grid;
  gap: 12px;
  max-height: 320px;
  overflow-y: auto;
`;

const LineItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  font-size: 0.94rem;
`;

const LiName = styled.span`
  color: #1f2d3d;
  min-width: 0;
`;

const LiQty = styled.span`
  color: #8a99a8;
  margin-left: 6px;
  font-size: 0.85rem;
`;

const LiPrice = styled.span`
  flex: 0 0 auto;
  color: #46586a;
  font-weight: 600;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(31, 45, 61, 0.1);
  margin: 16px 0;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  color: #556575;
  font-size: 0.98rem;

  strong {
    color: #1f2d3d;
    font-weight: 700;
  }

  &.grand {
    font-size: 1.1rem;
    strong {
      color: #0a8f57;
      font-size: 1.3rem;
      font-weight: 800;
    }
  }
`;
